/**
 * API client for Tutela Ignis backend
 */

import axios from 'axios';
import type {
  Alert,
  AlertCreateRequest,
  AreaRiskResponse,
  CountryRiskResponse,
  CountryRunInfo,
  FeatureImportance,
  Fire,
  HealthResponse,
  RealtimeRiskResponse,
  RiskLevel,
  RiskPrediction,
  RunComparison,
  RunSummary,
  SimulationRequest,
  SimulationResponse,
} from '../types';
import { getTurnstileToken } from '../hooks/useTurnstileToken';
import { supabase } from '../lib/supabase';

async function turnstileHeaders(): Promise<Record<string, string>> {
  try {
    const token = await getTurnstileToken();
    return token ? { 'Cf-Turnstile-Token': token } : {};
  } catch {
    return {};
  }
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach the Supabase access token (when signed in) so gated endpoints like
// /simulation/run can authorise the caller by their feature_grants.
api.interceptors.request.use(async (config) => {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // not signed in / supabase unconfigured, send unauthenticated
  }
  return config;
});

// Health check
export async function getHealth(): Promise<HealthResponse> {
  const { data } = await api.get<HealthResponse>('/health');
  return data;
}

// Risk predictions
export async function getRiskAtPoint(
  lat: number,
  lon: number,
  forDate?: string
): Promise<RiskPrediction> {
  const { data } = await api.post<RiskPrediction>('/risk/point', {
    lat,
    lon,
    for_date: forDate,
  });
  return data;
}

export async function getRiskInArea(
  bbox: { west: number; south: number; east: number; north: number },
  forDate?: string,
  minRisk?: RiskLevel
): Promise<AreaRiskResponse> {
  const { data } = await api.post<AreaRiskResponse>('/risk/area', {
    bbox_west: bbox.west,
    bbox_south: bbox.south,
    bbox_east: bbox.east,
    bbox_north: bbox.north,
    for_date: forDate,
    min_risk: minRisk,
  });
  return data;
}

// Fire history
export interface FiresParams {
  start_date?: string;
  end_date?: string;
  distrito?: string;
  lat?: number;
  lon?: number;
  radius_km?: number;
  limit?: number;
}

// Tier 1.3.b: R2-only frontend. Fire history is served from baked JSONs:
//  data.tutela.land/fires_recent.json   -> last 50 fires (hourly refresh)
//  data.tutela.land/fires_{year}.json   -> per-year history (daily refresh)
// All filters (date range, distrito, lat/lon/radius) run client-side.
export async function getFires(params: FiresParams = {}): Promise<Fire[]> {
  const cdn = import.meta.env.VITE_DATA_URL;
  if (!cdn) {
    // Dev / unconfigured: fall back to the API.
    const { data } = await api.get<Fire[]>('/fires', { params });
    return data;
  }

  // Decide which baked file(s) to fetch.
  let rows: Fire[] = [];

  if (params.start_date || params.end_date) {
    // Date range: load per-year files covering the range.
    const startYear = params.start_date
      ? new Date(params.start_date).getUTCFullYear()
      : 2002;
    const endYear = params.end_date
      ? new Date(params.end_date).getUTCFullYear()
      : new Date().getUTCFullYear();
    const years: number[] = [];
    for (let y = startYear; y <= endYear; y++) years.push(y);

    const responses = await Promise.all(
      years.map((y) =>
        axios
          .get<Fire[]>(`${cdn}/fires_${y}.json`, { timeout: 60000 })
          .then((r) => r.data)
          .catch(() => [] as Fire[]),
      ),
    );
    rows = responses.flat();
  } else {
    // No date filter: the dashboard / recent-fires use case.
    const { data } = await axios.get<Fire[]>(`${cdn}/fires_recent.json`, {
      timeout: 60000,
    });
    rows = data;
  }

  // Client-side filters.
  if (params.start_date) {
    rows = rows.filter((f) => (f as any).date >= params.start_date!);
  }
  if (params.end_date) {
    rows = rows.filter((f) => (f as any).date <= params.end_date!);
  }
  if (params.distrito) {
    const target = params.distrito.toLowerCase();
    rows = rows.filter(
      (f) => ((f as any).distrito ?? '').toLowerCase() === target,
    );
  }
  if (params.lat != null && params.lon != null) {
    const radius = params.radius_km ?? 50;
    rows = rows.filter((f) => {
      const dLat = ((f as any).lat - params.lat!) * 111;
      const dLon =
        ((f as any).lon - params.lon!) *
        111 *
        Math.cos((params.lat! * Math.PI) / 180);
      return Math.sqrt(dLat * dLat + dLon * dLon) <= radius;
    });
  }
  if (params.limit != null) {
    rows = rows.slice(0, params.limit);
  }
  return rows;
}

// Alerts
export async function createAlert(request: AlertCreateRequest): Promise<Alert> {
  const { data } = await api.post<Alert>('/alerts', request);
  return data;
}

export async function getAlert(alertId: string): Promise<Alert> {
  const { data } = await api.get<Alert>(`/alerts/${alertId}`);
  return data;
}

export async function deleteAlert(alertId: string): Promise<void> {
  await api.delete(`/alerts/${alertId}`);
}

// Realtime risk prediction
export async function getRealtimeRisk(
  lat: number,
  lon: number,
  runId?: string
): Promise<RealtimeRiskResponse> {
  const params: Record<string, unknown> = { lat, lon };
  if (runId) params.run_id = runId;
  const headers = await turnstileHeaders();
  const { data } = await api.get<RealtimeRiskResponse>('/risk/realtime', { params, headers });
  return data;
}

// Country-wide risk predictions
const DATA_CDN_URL = import.meta.env.VITE_DATA_URL;

export async function getCountryRisk(
  forDate?: string,
  runId?: string
): Promise<CountryRiskResponse> {
  // Serve from R2 CDN for default (latest) predictions, no backend load
  if (!forDate && !runId && DATA_CDN_URL) {
    const { data } = await axios.get<CountryRiskResponse>(
      `${DATA_CDN_URL}/country.json`,
      { timeout: 60000 },
    );
    return data;
  }
  // Fall back to API for specific run/date requests
  const params: Record<string, string> = {};
  if (forDate) params.for_date = forDate;
  if (runId) params.run_id = runId;
  const { data } = await api.get<CountryRiskResponse>('/risk/country', {
    params: Object.keys(params).length > 0 ? params : undefined,
    timeout: 60000,
  });
  return data;
}

// Country-wide forecast at a horizon (24h MVP; 48h/72h follow-up)
export async function getCountryForecast(horizonHours: number = 24): Promise<CountryRiskResponse> {
  // Try R2 CDN first
  if (DATA_CDN_URL) {
    try {
      const { data } = await axios.get<CountryRiskResponse>(
        `${DATA_CDN_URL}/country_forecast_${horizonHours}h.json`,
        { timeout: 60000 },
      );
      return data;
    } catch {
      // fall through to API
    }
  }
  const { data } = await api.get<CountryRiskResponse>('/risk/forecast', {
    params: { horizon_hours: horizonHours },
    timeout: 60000,
  });
  return data;
}

// Country prediction runs list (Tier 1.3.b, served from R2 via the worker's
// `bake_country_runs_json` task).
export async function getCountryRiskRuns(): Promise<CountryRunInfo[]> {
  if (DATA_CDN_URL) {
    try {
      const { data } = await axios.get<{ runs: CountryRunInfo[] }>(
        `${DATA_CDN_URL}/country_runs.json`,
        { timeout: 60000 },
      );
      return data.runs;
    } catch {
      // fall through to API for dev / when R2 file isn't there yet
    }
  }
  const { data } = await api.get<{ runs: CountryRunInfo[] }>('/risk/country/runs');
  return data.runs;
}

// Fire spread simulation
export async function runSimulation(
  request: SimulationRequest
): Promise<SimulationResponse> {
  const headers = await turnstileHeaders();
  const { data } = await api.post<SimulationResponse>('/simulation/run', request, { headers });
  return data;
}

// Training runs (Tier 1.3.b, served from R2 via the worker's `bake_runs_json`
// task; daily refresh).
export async function getRuns(): Promise<RunSummary[]> {
  if (DATA_CDN_URL) {
    try {
      const { data } = await axios.get<RunSummary[]>(`${DATA_CDN_URL}/runs.json`, {
        timeout: 60000,
      });
      return data;
    } catch {
      // fall through to API for dev / when R2 file isn't there yet
    }
  }
  const { data } = await api.get<RunSummary[]>('/runs');
  return data;
}

export async function getRunImportance(runId: string): Promise<FeatureImportance[]> {
  const { data } = await api.get<FeatureImportance[]>(`/runs/${runId}/importance`);
  return data;
}

export async function getRunComparison(
  runA: string,
  runB: string
): Promise<RunComparison> {
  const { data } = await api.get<RunComparison>('/runs/compare', {
    params: { run_a: runA, run_b: runB },
  });
  return data;
}

export default api;
